import type { AssessmentResult } from "@peer-start/platform-contracts";
import { Card } from "@peer-start/ui";
import { AlertTriangle, ShieldCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import { AssessmentAnswerReview } from "./AssessmentAnswerReview.js";
import { AssessmentBreakdown } from "./AssessmentBreakdown.js";
import { AssessmentLearningPath } from "./AssessmentLearningPath.js";
import { KnowledgeStatus } from "./KnowledgeStatus.js";

export function AssessmentResultView({ result }: { result: AssessmentResult }) {
  const { t, i18n } = useTranslation();
  return (
    <div className="space-y-5">
      <Card className="border-primary/20 bg-primary/5">
        {result.example ? (
          <p className="mb-3 inline-block rounded-full bg-warning px-3 py-1 text-xs font-medium text-warning-foreground">
            {t("knowledgeAssessment.exampleNotice")}
          </p>
        ) : null}
        <h2 className="text-2xl font-medium">{t("knowledgeAssessment.resultTitle")}</h2>
        <p className="mt-3 text-muted-foreground">
          {t("knowledgeAssessment.resultSummary", {
            correct: result.correct,
            total: result.total,
            mastered: result.skills.filter((skill) => skill.status === "mastered").length,
            skills: result.skills.length,
            required: result.learningPath.filter((part) => part.tested && part.required).length,
            parts: result.learningPath.filter((part) => part.tested).length,
          })}
        </p>
        {result.completedAt ? (
          <p className="mt-2 text-xs text-muted-foreground">
            {t("knowledgeAssessment.completedOn", {
              date: new Intl.DateTimeFormat(i18n.language, {
                dateStyle: "medium",
                timeStyle: "short",
              }).format(new Date(result.completedAt)),
            })}
          </p>
        ) : null}
        <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
          {(["mastered", "vulnerable", "learning_point", "blind_spot"] as const).map((status) => (
            <div className="rounded-xl border border-border bg-background p-3" key={status}>
              <strong className="mb-2 block text-3xl font-medium">
                {result.skills.filter((skill) => skill.status === status).length}
              </strong>
              <KnowledgeStatus status={status} />
            </div>
          ))}
        </div>
      </Card>
      {result.criticalWrongItemIds.length ? (
        <div
          role="status"
          className="flex gap-3 rounded-xl border border-destructive-foreground/20 bg-destructive p-4 text-destructive-foreground"
        >
          <AlertTriangle aria-hidden="true" className="size-5 shrink-0" />
          <p className="text-sm leading-relaxed">
            {t("knowledgeAssessment.criticalSignal", {
              count: result.criticalWrongItemIds.length,
              items: result.criticalWrongItemIds.join(", "),
            })}
          </p>
        </div>
      ) : null}
      <p className="flex gap-2 text-sm text-muted-foreground">
        <ShieldCheck aria-hidden="true" className="size-5 shrink-0" />
        {t("knowledgeAssessment.boundary")}
      </p>
      <AssessmentBreakdown result={result} />
      <AssessmentLearningPath result={result} />
      <AssessmentAnswerReview result={result} />
      <p className="text-xs text-muted-foreground">
        {t("knowledgeAssessment.resultVersion", {
          test: result.definition.version,
          rule: result.definition.rules.version,
        })}
      </p>
    </div>
  );
}
